import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../../i18n/LocalizedLink';
import { BlogLayout } from '../../templates/BlogLayout';
import { SEOMeta } from '../../templates/SEOMeta';
import ArticleContainer from '../../components/layout/ArticleContainer';

const BlogMultijugadorEs: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Modo Multijugador Online - El Impostor"
        description="Juega a El Impostor en modo multijugador online. Crea salas privadas, juega con amigos en tiempo real desde cualquier dispositivo."
        canonical="https://el-impostor.com/blog/modalidad/multijugador"
        keywords="impostor multijugador, impostor online, jugar impostor online, impostor tiempo real"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Inicio</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog/modalidad" className="text-indigo-600 dark:text-indigo-300 hover:underline">Modalidades</LocalizedLink>
        {' '}/ Multijugador
      </div>

      <ArticleContainer>
        <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-900 dark:text-indigo-200 mb-2">
          🌐 Modo Online Multijugador
        </h1>
        <p className="text-lg text-indigo-700 dark:text-indigo-300 font-semibold mb-6">
          Juega con amigos desde cualquier lugar del mundo en tiempo real
        </p>

        {/* ¿Qué es el modo online? */}
        <section id="que-es" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">¿Qué es el modo online?</h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-4">
            El modo online multijugador de Impostor Game te permite jugar con tus amigos sin importar dónde se encuentren.
            Solo necesitas compartir un código de sala y todos pueden unirse desde sus dispositivos móviles o computadoras.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-indigo-600 p-4 rounded-r-lg">
            <p className="font-semibold text-indigo-900 dark:text-indigo-200 mb-1">✨ Principal beneficio:</p>
            <p className="text-gray-700 dark:text-gray-200">
              Conecta con amigos, familiares o compañeros de trabajo desde cualquier ubicación. 
              Ideal para reuniones virtuales, clases online o simplemente para divertirse a distancia.
            </p>
          </div>
        </section>

        {/* Características principales */}
        <section id="caracteristicas" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Características principales</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Conexión en tiempo real:</strong> Todos los jugadores ven las actualizaciones al instante</li>
            <li><strong>Multisala:</strong> Varias partidas pueden jugarse simultáneamente</li>
            <li><strong>Sin instalación:</strong> Solo necesitas un navegador web</li>
            <li><strong>Compatible con móviles:</strong> Juega desde cualquier dispositivo</li>
            <li><strong>Chat por rondas:</strong> Debate estratégicamente en cada fase del juego</li>
            <li><strong>Votaciones anónimas:</strong> Nadie sabe quién votó a quién hasta el final</li>
          </ul>
        </section>

        {/* Cómo jugar */}
        <section id="como-jugar" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Cómo jugar paso a paso</h2>
          <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200 space-y-3">
            <li><strong>Crear o unirse a una sala:</strong> El anfitrión crea una sala y comparte el código</li>
            <li><strong>Esperar jugadores:</strong> Los demás se unen usando el código de 6 caracteres</li>
            <li><strong>Asignación de roles:</strong> El sistema asigna automáticamente roles (jugadores e impostores)</li>
            <li><strong>Rondas de juego:</strong> En cada ronda, escribe mensajes y lee lo que escriben otros</li>
            <li><strong>Debate:</strong> Usa la lógica para identificar comportamientos sospechosos</li>
            <li><strong>Votación:</strong> Al final de cada ronda, vota para expulsar a un sospechoso</li>
            <li><strong>Victoria:</strong> Los jugadores ganan eliminando al impostor, los impostores ganan pasando desapercibidos</li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <LocalizedLink to="/create" className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Crear sala ahora
            </LocalizedLink>
            <LocalizedLink to="/join" className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors">
              Unirme a una sala
            </LocalizedLink>
          </div>
        </section>

        {/* Casos de uso */}
        <section id="casos-uso" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Casos de uso ideales</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-blue-200 dark:border-gray-800">
              <h3 className="font-bold text-indigo-800 dark:text-indigo-200 mb-2">👥 Reuniones virtuales</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Perfecto para romper el hielo en videollamadas de trabajo o eventos online</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-purple-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🎓 Educación online</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Herramienta dinámica para clases virtuales y desarrollo de pensamiento crítico</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-green-200 dark:border-gray-800">
              <h3 className="font-bold text-green-800 dark:text-green-200 mb-2">🎮 Gaming con amigos</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Organiza partidas con amigos de diferentes ciudades o países</p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-pink-200 dark:border-gray-800">
              <h3 className="font-bold text-pink-800 dark:text-pink-200 mb-2">📺 Streaming</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Ideal para streamers que quieren interactuar con su comunidad</p>
            </div>
          </div>
        </section>

        {/* Consejos */}
        <section id="consejos" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Consejos para el modo online</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Usa videollamada paralela:</strong> Combina el juego con Discord, Zoom o Meet para más inmersión</li>
            <li><strong>Establece reglas claras:</strong> Define tiempo de debate y normas antes de empezar</li>
            <li><strong>Mantén grupos equilibrados:</strong> Ideal entre 4-8 jugadores para mejores partidas</li>
            <li><strong>Conexión estable:</strong> Asegúrate de tener buena conexión a internet</li>
            <li><strong>Sé respetuoso:</strong> Mantén un ambiente amigable y divertido para todos</li>
          </ul>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Preguntas frecuentes</h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Necesito cuenta para jugar?</p>
              <p className="text-gray-700 dark:text-gray-200">No, solo necesitas un nombre para entrar a la sala.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Cuántos jugadores pueden unirse?</p>
              <p className="text-gray-700 dark:text-gray-200">Recomendamos entre 3 y 10 jugadores para una mejor experiencia.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Funciona en móvil?</p>
              <p className="text-gray-700 dark:text-gray-200">Sí, está completamente optimizado para dispositivos móviles.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Cuánto dura una partida?</p>
              <p className="text-gray-700 dark:text-gray-200">Entre 5 y 15 minutos, dependiendo del número de jugadores y rondas.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Puedo jugar en privado?</p>
              <p className="text-gray-700 dark:text-gray-200">Sí, solo comparte el código con las personas que quieras invitar.</p>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <div className="mt-8 p-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl">
          <h3 className="text-2xl font-bold mb-2">¿Listo para jugar online?</h3>
          <p className="mb-4 text-indigo-100">Crea una sala e invita a tus amigos en segundos. ¡La diversión está a un clic de distancia!</p>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink to="/create" className="px-6 py-3 bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-200 rounded-lg font-bold hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              🏠 Crear sala
            </LocalizedLink>
            <LocalizedLink to="/join" className="px-6 py-3 bg-indigo-500 border-2 border-white text-white rounded-lg font-bold hover:bg-indigo-400 transition-colors">
              🚪 Unirse a sala
            </LocalizedLink>
            <LocalizedLink to="/" className="px-6 py-3 text-white underline font-medium hover:text-indigo-100 transition-colors">
              Volver al inicio
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogMultijugadorPt: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Modo Multijogador Online - El Impostor"
        description="Jogue El Impostor no modo multijogador online. Crie salas privadas e jogue com amigos em tempo real de qualquer dispositivo."
        canonical="https://el-impostor.com/blog/modalidad/multijugador"
        keywords="impostor multijogador, impostor online, jogar impostor online, impostor tempo real"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Início</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog/modalidad" className="text-indigo-600 dark:text-indigo-300 hover:underline">Modalidades</LocalizedLink>
        {' '}/ Multijogador
      </div>

      <ArticleContainer>
        <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-900 dark:text-indigo-200 mb-2">
          🌐 Modo Online Multijogador
        </h1>
        <p className="text-lg text-indigo-700 dark:text-indigo-300 font-semibold mb-6">
          Jogue com amigos de qualquer lugar do mundo em tempo real
        </p>

        {/* O que é o modo online? */}
        <section id="que-es" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">O que é o modo online?</h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-4">
            O modo online multijogador do Impostor Game permite jogar com seus amigos não importa onde estejam.
            Você só precisa compartilhar um código de sala e todos podem entrar por celular ou computador.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-indigo-600 p-4 rounded-r-lg">
            <p className="font-semibold text-indigo-900 dark:text-indigo-200 mb-1">✨ Principal benefício:</p>
            <p className="text-gray-700 dark:text-gray-200">
              Conecte-se com amigos, familiares ou colegas de trabalho de qualquer lugar.
              Ideal para reuniões virtuais, aulas online ou simplesmente para se divertir à distância.
            </p>
          </div>
        </section>

        {/* Características principais */}
        <section id="caracteristicas" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Características principais</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Conexão em tempo real:</strong> Todos os jogadores veem as atualizações instantaneamente</li>
            <li><strong>Multissala:</strong> Várias partidas podem acontecer ao mesmo tempo</li>
            <li><strong>Sem instalação:</strong> Você só precisa de um navegador</li>
            <li><strong>Compatível com celular:</strong> Jogue em qualquer dispositivo</li>
            <li><strong>Chat por rodadas:</strong> Debata estrategicamente em cada fase do jogo</li>
            <li><strong>Votação anônima:</strong> Ninguém sabe quem votou em quem até o final</li>
          </ul>
        </section>

        {/* Como jogar */}
        <section id="como-jugar" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Como jogar passo a passo</h2>
          <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200 space-y-3">
            <li><strong>Criar ou entrar em uma sala:</strong> O anfitrião cria a sala e compartilha o código</li>
            <li><strong>Aguardar jogadores:</strong> Os demais entram usando o código de 6 caracteres</li>
            <li><strong>Atribuição de papéis:</strong> O sistema atribui automaticamente papéis (jogadores e impostores)</li>
            <li><strong>Rodadas do jogo:</strong> Em cada rodada, escreva mensagens e leia o que os outros escreveram</li>
            <li><strong>Debate:</strong> Use a lógica para identificar comportamentos suspeitos</li>
            <li><strong>Votação:</strong> Ao final de cada rodada, vote para expulsar um suspeito</li>
            <li><strong>Vitória:</strong> Jogadores vencem eliminando o impostor, impostores vencem passando despercebidos</li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <LocalizedLink to="/create" className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Criar sala agora
            </LocalizedLink>
            <LocalizedLink to="/join" className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors">
              Entrar em uma sala
            </LocalizedLink>
          </div>
        </section>

        {/* Casos de uso */}
        <section id="casos-uso" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Casos de uso ideais</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-blue-200 dark:border-gray-800">
              <h3 className="font-bold text-indigo-800 dark:text-indigo-200 mb-2">👥 Reuniões virtuais</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Perfeito para quebrar o gelo em videochamadas de trabalho ou eventos online</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-purple-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🎓 Educação online</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Ferramenta dinâmica para aulas virtuais e desenvolvimento do pensamento crítico</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-green-200 dark:border-gray-800">
              <h3 className="font-bold text-green-800 dark:text-green-200 mb-2">🎮 Jogos com amigos</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Organize partidas com amigos de diferentes cidades ou países</p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-pink-200 dark:border-gray-800">
              <h3 className="font-bold text-pink-800 dark:text-pink-200 mb-2">📺 Streaming</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Ideal para streamers que querem interagir com a comunidade</p>
            </div>
          </div>
        </section>

        {/* Dicas */}
        <section id="consejos" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Dicas para o modo online</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Use uma chamada de vídeo paralela:</strong> Combine o jogo com Discord, Zoom ou Meet para mais imersão</li>
            <li><strong>Defina regras claras:</strong> Estabeleça tempo de debate e normas antes de começar</li>
            <li><strong>Mantenha grupos equilibrados:</strong> Ideal entre 4–8 jogadores para melhores partidas</li>
            <li><strong>Conexão estável:</strong> Garanta uma boa conexão com a internet</li>
            <li><strong>Seja respeitoso:</strong> Mantenha um ambiente amigável e divertido para todos</li>
          </ul>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-10">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Perguntas frequentes</h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Preciso de uma conta para jogar?</p>
              <p className="text-gray-700 dark:text-gray-200">Não, basta um nome para entrar na sala.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Quantos jogadores podem entrar?</p>
              <p className="text-gray-700 dark:text-gray-200">Recomendamos 3 a 10 jogadores para a melhor experiência.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Funciona no celular?</p>
              <p className="text-gray-700 dark:text-gray-200">Sim, é totalmente otimizado para dispositivos móveis.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Quanto dura uma partida?</p>
              <p className="text-gray-700 dark:text-gray-200">Cerca de 5 a 15 minutos, dependendo dos jogadores e rodadas.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Posso jogar de forma privada?</p>
              <p className="text-gray-700 dark:text-gray-200">Sim — compartilhe o código apenas com quem deseja convidar.</p>
            </div>
          </div>
        </section>

        {/* Call-to-action */}
        <div className="bg-indigo-50 dark:bg-gray-900 border border-indigo-200 dark:border-gray-800 rounded-xl p-6 text-center">
          <h3 className="text-2xl font-bold text-indigo-900 dark:text-indigo-200 mb-2">Pronto para jogar online?</h3>
          <p className="text-gray-700 dark:text-gray-200 mb-4">Crie uma sala e convide seus amigos em segundos. A diversão está a um clique!</p>
          <div className="flex flex-wrap justify-center gap-3">
            <LocalizedLink to="/create" className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Criar sala
            </LocalizedLink>
            <LocalizedLink to="/join" className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors">
              Entrar na sala
            </LocalizedLink>
            <LocalizedLink to="/" className="px-6 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              Voltar ao início
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogMultijugadorEn: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Online Multiplayer Mode - Impostor Game"
        description="Play Impostor Game in online multiplayer mode. Create private rooms, play with friends in real time from any device."
        canonical="https://el-impostor.com/blog/modalidad/multijugador"
        keywords="impostor multiplayer, impostor online, play impostor online, impostor real time"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Home</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog/modalidad" className="text-indigo-600 dark:text-indigo-300 hover:underline">Game modes</LocalizedLink>
        {' '}/ Multiplayer
      </div>

      <ArticleContainer>
        <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-900 dark:text-indigo-200 mb-2">
          🌐 Online Multiplayer Mode
        </h1>
        <p className="text-lg text-indigo-700 dark:text-indigo-300 font-semibold mb-6">
          Play with friends from anywhere in the world in real time
        </p>

        {/* What is online mode? */}
        <section id="que-es" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">What is online mode?</h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-4">
            Impostor Game’s online multiplayer mode lets you play with friends no matter where they are.
            Just share a room code and everyone can join from their phone, tablet, or computer.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-indigo-600 p-4 rounded-r-lg">
            <p className="font-semibold text-indigo-900 dark:text-indigo-200 mb-1">✨ Key benefit:</p>
            <p className="text-gray-700 dark:text-gray-200">
              Connect with friends, family, or coworkers from anywhere.
              Great for virtual meetups, online classes, or simply having fun remotely.
            </p>
          </div>
        </section>

        {/* Key features */}
        <section id="caracteristicas" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Key features</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Real-time connection:</strong> Everyone sees updates instantly</li>
            <li><strong>Multiple rooms:</strong> Several matches can run at the same time</li>
            <li><strong>No installation:</strong> You only need a web browser</li>
            <li><strong>Mobile-friendly:</strong> Play from any device</li>
            <li><strong>Round-based chat:</strong> Debate strategically in each phase</li>
            <li><strong>Anonymous voting:</strong> Nobody knows who voted for whom until the end</li>
          </ul>
        </section>

        {/* How to play */}
        <section id="como-jugar" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">How to play step by step</h2>
          <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200 space-y-3">
            <li><strong>Create or join a room:</strong> The host creates a room and shares the code</li>
            <li><strong>Wait for players:</strong> Others join using the 6-character code</li>
            <li><strong>Role assignment:</strong> Roles are assigned automatically (players and impostors)</li>
            <li><strong>Rounds:</strong> Each round, write messages and read what others write</li>
            <li><strong>Debate:</strong> Use logic to spot suspicious behavior</li>
            <li><strong>Vote:</strong> At the end of each round, vote to eliminate a suspect</li>
            <li><strong>Win:</strong> Players win by eliminating the impostor; impostors win by staying hidden</li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <LocalizedLink
              to="/create"
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Create a room now
            </LocalizedLink>
            <LocalizedLink
              to="/join"
              className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
            >
              Join a room
            </LocalizedLink>
          </div>
        </section>

        {/* Use cases */}
        <section id="casos-uso" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Ideal use cases</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-blue-200 dark:border-gray-800">
              <h3 className="font-bold text-indigo-800 mb-2">👥 Virtual meetups</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Great for icebreakers in work calls or online events</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-purple-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 mb-2">🎓 Online education</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">A dynamic tool for virtual classes and critical thinking</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-green-200 dark:border-gray-800">
              <h3 className="font-bold text-green-800 mb-2">🎮 Gaming with friends</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Organize matches with friends in different cities or countries</p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-pink-200 dark:border-gray-800">
              <h3 className="font-bold text-pink-800 mb-2">📺 Streaming</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Perfect for streamers who want to engage their community</p>
            </div>
          </div>
        </section>

        {/* Tips */}
        <section id="consejos" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Tips for online mode</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Use a parallel voice call:</strong> Pair the game with Discord, Zoom, or Meet for more immersion</li>
            <li><strong>Set clear rules:</strong> Agree on debate time and guidelines before starting</li>
            <li><strong>Keep groups balanced:</strong> 4–8 players is ideal for best matches</li>
            <li><strong>Stable connection:</strong> Make sure your internet is reliable</li>
            <li><strong>Be respectful:</strong> Keep a friendly, fun environment for everyone</li>
          </ul>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Frequently asked questions</h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Do I need an account to play?</p>
              <p className="text-gray-700 dark:text-gray-200">No—just choose a name to enter the room.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">How many players can join?</p>
              <p className="text-gray-700 dark:text-gray-200">We recommend 3 to 10 players for the best experience.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Does it work on mobile?</p>
              <p className="text-gray-700 dark:text-gray-200">Yes, it’s fully optimized for mobile devices.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">How long does a match last?</p>
              <p className="text-gray-700 dark:text-gray-200">About 5 to 15 minutes depending on player count and rounds.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Can I play privately?</p>
              <p className="text-gray-700 dark:text-gray-200">Yes—share the code only with the people you want to invite.</p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <div className="mt-8 p-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl">
          <h3 className="text-2xl font-bold mb-2">Ready to play online?</h3>
          <p className="mb-4 text-indigo-100">
            Create a room and invite your friends in seconds. Fun is just one click away!
          </p>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink
              to="/create"
              className="px-6 py-3 bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-200 rounded-lg font-bold hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              🏠 Create room
            </LocalizedLink>
            <LocalizedLink
              to="/join"
              className="px-6 py-3 bg-indigo-500 border-2 border-white text-white rounded-lg font-bold hover:bg-indigo-400 transition-colors"
            >
              🚪 Join room
            </LocalizedLink>
            <LocalizedLink
              to="/"
              className="px-6 py-3 text-white underline font-medium hover:text-indigo-100 transition-colors"
            >
              Back to home
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogMultijugador: React.FC = () => {
  const { i18n } = useTranslation();
  if (i18n.language === 'en') return <BlogMultijugadorEn />;
  if (i18n.language === 'pt') return <BlogMultijugadorPt />;
  return <BlogMultijugadorEs />;
};

export default BlogMultijugador;
